import { of, from } from 'rxjs';
import { switchMap, catchError, mergeMap, map } from 'rxjs/operators';
import { Epic, ofType } from 'redux-observable';

import Axios, { AxiosError } from 'axios';
import { hopplerApi } from '../../../../constants/api';

import * as ListingActions from './listing.actions'
import { ListingAction } from './listing.actions';

import { Listing } from '../../../../types/listing.model';
import { showToast } from '../../../utils/hoppler-toast.service';
import { ListingService } from '../../../utils/listing.service';

let listingEffects: Epic[] = [];
let listingService = new ListingService();

const getApiProperty: Epic = getApiProperty$ => getApiProperty$.pipe(
  ofType<ListingAction>(ListingActions.GET_API_PROPERTY),
  switchMap((action: ListingAction) => {

    const focusedPropertyKey: string = action.payload.focusedPropertyKey; 

    let promise = Axios.get<Listing>(
      `${hopplerApi.host}/api/properties/${focusedPropertyKey.toUpperCase()}/preview`,
      {
        withCredentials: true
      }
    );

    return from(promise).pipe(
      mergeMap((response) => {

        return of({
          type: ListingActions.GET_API_PROPERTY_SUCCESS,
          payload: {
            focusedListing: response.data
          }
        } as ListingAction);  
      }), catchError(errRes => {

        let message = errRes.response.data.msg ? errRes.response.data.msg : null;
      
        showToast({
          title: 'Failed to retrieve listing details',
          message: message,
          duration: 5000
        });

        return of({
          type: ListingActions.GET_API_PROPERTY_FAIL
        } as ListingAction);
      })
    );
  })
);

const expireListing: Epic = expireListing$ => expireListing$.pipe(
  ofType<ListingAction>(ListingActions.EXPIRE_LISTING),
  switchMap((action: ListingAction) => {

    const focusedPropertyKey: string = action.payload.focusedPropertyKey;

    let promise = Axios({
      url: `${hopplerApi.host}/api/properties/${focusedPropertyKey}/expire`,
      method: 'POST',
      withCredentials: true
    })

    return from(promise).pipe(
      mergeMap((response) => {

        showToast({
          title: 'Listing is now expired',
          message: response.data.msg,
          duration: 5000
        });

        setTimeout(() => {
          location.reload();
        }, 1000);

        return of({
          type: ListingActions.EXPIRE_LISTING_SUCCESS,
          payload: {
            focusedListing: response.data
          }
        } as ListingAction);  
             
      }), catchError(errRes => {

        let message = errRes.response.data.msg ? errRes.response.data.msg : null;
      
        showToast({
          title: 'Failed to expire listing',
          message: message,
          duration: 5000
        });

        return of({
          type: ListingActions.EXPIRE_LISTING_FAIL
        } as ListingAction);
      })
    );
  })
);

const refreshListing: Epic = refreshListing$ => refreshListing$.pipe(
  ofType<ListingAction>(ListingActions.REFRESH_LISTING),
  switchMap((action: ListingAction) => {

    const focusedPropertyKey: string = action.payload.focusedPropertyKey;

    let promise = Axios({
      url: `${hopplerApi.host}/api/properties/${focusedPropertyKey}/refresh`,
      method: 'PUT',
      withCredentials: true
    })

    return from(promise).pipe(
      mergeMap((response) => {

        showToast({
          title: 'Refresh listing success',
          message: response.data.msg,
          duration: 5000
        });

        setTimeout(() => {
          location.reload();
        }, 1000);

        return of({
          type: ListingActions.REFRESH_LISTING_SUCCESS,
          payload: {
            focusedListing: response.data
          }
        } as ListingAction);  
             
      }), catchError(errRes => {

        let message = errRes.response.data.msg ? errRes.response.data.msg : null;
      
        showToast({
          title: 'Failed to refresh listing',
          message: message,
          duration: 5000
        });

        return of({
          type: ListingActions.REFRESH_LISTING_FAIL
        } as ListingAction);
      })
    );
  })
);

const getListingDetail: Epic = getListingDetail$ => getListingDetail$.pipe(
  ofType<ListingAction>(ListingActions.GET_LISTING_DETAIL),
  switchMap((action: ListingAction) => {

    const focusedPropertyKey: string = action.payload.focusedPropertyKey; 

    let promise = Axios.get<Listing>(
      `${hopplerApi.host}/kumita/listing/${focusedPropertyKey}`,
      {
        withCredentials: true
      }
    );

    return from(promise).pipe(
      mergeMap((response) => {

        return of({
          type: ListingActions.GET_LISTING_DETAIL_SUCCESS,
          payload: {
            focusedListing: response.data
          }
        } as ListingAction);  
      }), catchError(errRes => {

        let message = errRes.response.data.msg ? errRes.response.data.msg : null;
      
        showToast({
          title: 'Failed to retrieve listing details',
          message: message,
          duration: 5000
        });

        return of({
          type: ListingActions.GET_LISTING_DETAIL_FAIL
        } as ListingAction);
      })
    );
  })
);


const getListingData: Epic = getDashboardData$ => getDashboardData$.pipe(
  ofType<ListingAction>(ListingActions.GET_LISTING_DATA),
  switchMap((action: ListingAction) => {
    // /kumita/partner-broker/dashboard/listing/
    // let promise = Axios.get<ListResponse<Listing>>(`${hopplerApi.host}/kumita/partner-broker/dashboard/listing/${action.payload.gridStage}`, 
    let promise = listingService.getListings({
      stage: action.payload.gridStage,
      groupProfile: action.payload.groupProfile,
      filters: action.payload.gridFilters
    });

    return from(promise).pipe(
      mergeMap((response) => {
        const { results, count, aveAreaSqm, avePrice, avePricePerSqm } = response.data;

        return of({
          type: ListingActions.GET_LISTING_DATA_SUCCESS,
          payload: {
            listings: results,
            totalListings: count,
            ppsqm: {
              averageAreaSqm: aveAreaSqm,
              averagePrice: avePrice,
              averagePricePerSqm: avePricePerSqm
            }
          }
        } as ListingAction);
      }),
      catchError((errRes) => {
        let message = errRes.response.data.msg ? errRes.response.data.msg : null;
      
        showToast({
          title: 'Failed to retrieve listing data',
          message: message,
          duration: 5000
        });

        return of({
          type: ListingActions.GET_LISTING_DATA_FAIL
        } as ListingAction);
      })
    );
  })
);

const postProperty: Epic = postProperty$ => postProperty$.pipe(
  ofType<ListingAction>(ListingActions.POST_PROPERTY),
  switchMap((action: ListingAction) => {
    const apiProperty = action.payload.apiProperty;

    let promise = Axios.post(
      `${hopplerApi.host}/kumita/listing`,
      apiProperty,
      {
        withCredentials: true
      }
    );

    return from(promise).pipe(
      mergeMap((response) => {

        return of({
          type: ListingActions.POST_PROPERTY_SUCCESS,
          payload: {
          }
        } as ListingAction);
      }),
      catchError((errRes) => {

        let message = errRes.response.data.msg ? errRes.response.data.msg : null;
      
        showToast({
          title: 'Failed to submit listing',
          message: message,
          duration: 5000
        });

        return of({
          type: ListingActions.POST_PROPERTY_FAIL
        } as ListingAction);
      })
    );
  })
);

const saveAsDraft: Epic = saveAsDraft$ => saveAsDraft$.pipe(
  ofType<ListingAction>(ListingActions.SAVE_AS_DRAFT),
  switchMap((action: ListingAction) => {
    const apiProperty = action.payload.apiProperty;

    let promise = Axios.post(
      `${hopplerApi.host}/kumita/listing`,
      apiProperty,
      {
        withCredentials: true
      }
    );

    return from(promise).pipe(
      mergeMap((response) => {

        showToast({
          title: 'Successfully saved draft',
          message: 'Your property upload has been saved as draft',
          duration: 5000
        });

        return of({
          type: ListingActions.SAVE_AS_DRAFT_SUCCESS,
          payload: {
          }
        } as ListingAction);
      }),
      catchError((errRes) => {

        let message = errRes.response.data.msg ? errRes.response.data.msg : null;
      
        showToast({
          title: 'Failed to save as draft',
          message: message,
          duration: 5000
        });

        return of({
          type: ListingActions.SAVE_AS_DRAFT_FAIL
        } as ListingAction);
      })
    );
  })
);

const generateDescriptionPrefill: Epic = generateDescriptionPrefill$ => generateDescriptionPrefill$.pipe(
  ofType<ListingAction>(ListingActions.GENERATE_DESCRIPTION_PREFILL),
  switchMap((action: ListingAction) => {

    const incompleteListing = action.payload.apiProperty;

    let promise = Axios.post<any>(
      `${hopplerApi.host}/kumita/listing/description`,
      incompleteListing,
      {
        withCredentials: true
      }
    );

    return from(promise).pipe(
      mergeMap((response) => {

        return of({
          type: ListingActions.GENERATE_DESCRIPTION_PREFILL_SUCCESS,
          payload: {
            descriptionPrefill: response.data.msg
          }
        } as ListingAction);  
      }), catchError(errRes => {
        let message = errRes.response.data.msg ? errRes.response.data.msg : null;
      
        showToast({
          title: 'Failed to retrieve generated description',
          message: message,
          duration: 5000
        });

        return of({
          type: ListingActions.GENERATE_DESCRIPTION_PREFILL_FAIL
        } as ListingAction);
      })
    );
  })
);



const updateProperty: Epic = updateProperty$ => updateProperty$.pipe(
  ofType<ListingAction>(ListingActions.UPDATE_PROPERTY),
  switchMap((action: ListingAction) => {
    const listing: Listing = action.payload.listingToUpdate;

    let promise = Axios.put(
      `${hopplerApi.host}/kumita/listing/${listing.propertyKey}`,
      listing,
      {
        withCredentials: true
      }
    );

    return from(promise).pipe(
      mergeMap((response) => {
        showToast({
          title: 'Successfully updated listing',
          message: 'Your property has been been updated',
          duration: 5000
        });

        setTimeout(() => {
          location.reload();
        }, 1000);

        return of({
          type: ListingActions.UPDATE_PROPERTY_SUCCESS,
          payload: {
          }
        } as ListingAction);
      }),
      catchError((errRes: AxiosError) => {

        let message = errRes.response.data.msg ? errRes.response.data.msg : null;
      
        showToast({
          title: 'Failed to update listing',
          message: message,
          duration: 5000
        });

        return of({
          type: ListingActions.UPDATE_PROPERTY_FAIL
        } as ListingAction);
      })
    );
  })
);

// http://api.hoppler.com.ph';
// http://devapi.hoppler.com.ph/api/properties/RR2414382/delete
const deleteProperty: Epic = deleteProperty$ => deleteProperty$.pipe(
  ofType<ListingAction>(ListingActions.DELETE_LISING),
  switchMap((action: ListingAction) => {

    const { focusedPropertyKey, reason } = action.payload;
    console.log({ payloadFromListingEffect: action.payload })

    let promise = Axios({
      url: `${hopplerApi.host}/api/properties/${focusedPropertyKey}/delete`,
      data: { reason },
      method: 'POST',
      withCredentials: true
    })

    return from(promise).pipe(
      mergeMap((response) => {
        showToast({
          title: 'Successfully delete listing',
          message: 'Your property has been been deleted',
          duration: 5000
        });

        setTimeout(() => {
          location.reload();
        }, 1000);

        return of({
          type: ListingActions.DELETE_LISTING_SUCCESS,
          payload: {
          }
        } as ListingAction);
      }),
      catchError((errRes: AxiosError) => {

        let message = errRes.response.data.msg ? errRes.response.data.msg : null;

        showToast({
          title: 'Failed to delete listing',
          message: message,
          duration: 5000
        });

        return of({
          type: ListingActions.DELETE_LISTING_FAIL
        } as ListingAction);
      })
    );
  })
)

// ! Make sure to add created epics to array 

listingEffects.push(getListingData);
listingEffects.push(postProperty);
listingEffects.push(saveAsDraft);
listingEffects.push(getListingDetail);
listingEffects.push(generateDescriptionPrefill);
listingEffects.push(updateProperty);
listingEffects.push(getApiProperty);
listingEffects.push(refreshListing);
listingEffects.push(expireListing);
listingEffects.push(deleteProperty);

// listingEffects.push(getListingDetailChain);

export default listingEffects;